import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  useMediaQuery,
  Chip,
  Stack,
  Fade,
  Slide,
  Zoom,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Settings,
  Logout,
  DarkMode,
  LightMode,
  Refresh,
  Search,
  Apps,
  Fullscreen,
  FullscreenExit,
  Speed,
  TrendingUp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { useAppContext } from '../../context/AppContext';
// import { useSocket } from '../../context/SocketContext';
import NotificationPanel from './NotificationPanel';
import { brandColors, createGradientText } from '../../utils/theme';

function Header({ drawerWidth, isMobile: propIsMobile, onMobileNavOpen }) {
  const theme = useTheme();
  const isLegacyMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = propIsMobile !== undefined ? propIsMobile : isLegacyMobile;
  
  const {
    user,
    sidebarOpen,
    toggleSidebar,
    darkMode,
    toggleDarkMode,
    notifications,
    unreadCount,
    logout,
  } = useAppContext();

  // const { isConnected } = useSocket();
  const isConnected = true; // Mock for now

  // Enhanced state management
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Auto-hide header on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHeaderVisible(currentScrollY < lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Fullscreen toggle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
    }
  };

  // Mock performance data
  const performanceData = {
    responseTime: '245ms',
    uptime: '99.9%',
    activeUsers: 42,
  };

  return (
    <Slide direction="down" in={headerVisible} timeout={300}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          ml: {
            lg: sidebarOpen ? `${drawerWidth}px` : 0,
          },
          width: {
            lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
          },
          transition: theme.transitions.create(['width', 'margin', 'transform'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          background: darkMode 
            ? 'rgba(15, 23, 42, 0.8)'
            : 'rgba(248, 250, 252, 0.8)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: darkMode
            ? '0 4px 30px 0 rgba(0, 0, 0, 0.3)'
            : '0 4px 30px 0 rgba(31, 38, 135, 0.1)',
          borderBottom: darkMode 
            ? '1px solid rgba(241, 245, 249, 0.1)' 
            : '1px solid rgba(30, 41, 59, 0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          {/* Enhanced Menu Button - Mobile/Desktop adaptive */}
          <Tooltip title={isMobile ? "Open Navigation" : "Toggle Sidebar"}>
            <IconButton
              color="inherit"
              aria-label={isMobile ? "open navigation" : "toggle sidebar"}
              onClick={isMobile ? onMobileNavOpen : toggleSidebar}
              edge="start"
              sx={{ 
                mr: 2,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                minHeight: 44, // Touch-friendly
                minWidth: 44,
                '&:hover': {
                  background: 'rgba(59, 130, 246, 0.1)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          {/* Enhanced App Title */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              noWrap
              component="div"
              sx={{
                ...createGradientText(brandColors.primary.gradient),
                fontWeight: 700,
                display: { xs: isMobile ? 'block' : 'none', sm: 'block' },
                letterSpacing: '-0.02em',
                fontSize: isMobile ? '1.1rem' : undefined,
              }}
            >
              KashLegal
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                ml: { xs: 0, sm: 1 },
                fontWeight: 400,
                color: 'text.secondary',
                display: { xs: 'none', md: isMobile ? 'none' : 'block' },
                fontSize: isMobile ? '0.9rem' : undefined,
              }}
            >
              GBP Manager
            </Typography>

            {/* Performance Badge */}
            {!isMobile && (
              <Fade in timeout={1000}>
                <Chip
                  icon={<Speed />}
                  label={`${performanceData.responseTime} • ${performanceData.activeUsers} users`}
                  size="small"
                  sx={{
                    ml: 3,
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: brandColors.accent.main,
                    border: `1px solid ${brandColors.accent.main}30`,
                    fontWeight: 500,
                    '& .MuiChip-icon': {
                      color: brandColors.accent.main,
                    },
                  }}
                />
              </Fade>
            )}
          </Box>

          {/* Enhanced Connection Status */}
          {!isMobile && (
            <Zoom in timeout={800}>
              <Chip
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: isConnected ? brandColors.accent.main : brandColors.secondary.main,
                      animation: isConnected ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                }
                label={isConnected ? 'Connected' : 'Disconnected'}
                size="small"
                sx={{
                  mr: 2,
                  background: isConnected 
                    ? 'rgba(16, 185, 129, 0.1)' 
                    : 'rgba(220, 38, 38, 0.1)',
                  color: isConnected ? brandColors.accent.main : brandColors.secondary.main,
                  border: `1px solid ${isConnected ? brandColors.accent.main : brandColors.secondary.main}30`,
                  fontWeight: 500,
                }}
              />
            </Zoom>
          )}

          {/* Enhanced Action Buttons - Mobile Optimized */}
          <Stack direction="row" spacing={isMobile ? 0.5 : 1} alignItems="center">
            {/* Fullscreen Toggle */}
            {!isMobile && (
              <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
                <IconButton
                  color="inherit"
                  onClick={toggleFullscreen}
                  sx={{
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    minHeight: 44,
                    minWidth: 44,
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            )}

            {/* Search Button - Show on larger mobile */}
            {!isMobile && (
              <Tooltip title="Global Search">
                <IconButton
                  color="inherit"
                  sx={{
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    minHeight: 44,
                    minWidth: 44,
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Search />
                </IconButton>
              </Tooltip>
            )}

            {/* Refresh Button - Mobile hidden */}
            {!isMobile && (
              <Tooltip title="Refresh Data">
                <IconButton
                  color="inherit"
                  onClick={handleRefresh}
                  sx={{
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    minHeight: 44,
                    minWidth: 44,
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.1)',
                      transform: 'scale(1.05) rotate(180deg)',
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}

            {/* Dark Mode Toggle - Always show but smaller on mobile */}
            <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton 
                color="inherit" 
                onClick={toggleDarkMode}
                sx={{
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                  minWidth: 44,
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Zoom in timeout={300}>
                  <Box>
                    {darkMode ? <LightMode /> : <DarkMode />}
                  </Box>
                </Zoom>
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={handleNotificationOpen}
                aria-label={`${unreadCount} notifications`}
                sx={{
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                  minWidth: 44,
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      background: brandColors.secondary.gradient,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.6rem' : '0.75rem',
                      minWidth: isMobile ? '16px' : '20px',
                      height: isMobile ? '16px' : '20px',
                      animation: unreadCount > 0 ? 'bounce 2s infinite' : 'none',
                      '@keyframes bounce': {
                        '0%, 20%, 60%, 100%': { transform: 'translateY(0)' },
                        '40%': { transform: 'translateY(-3px)' },
                        '80%': { transform: 'translateY(-1px)' },
                      },
                    },
                  }}
                >
                  <Notifications sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Tooltip title="Account Menu">
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{
                  ml: isMobile ? 0.5 : 1,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                  minWidth: 44,
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {user?.avatar ? (
                  <Avatar
                    src={user.avatar}
                    alt={user.name}
                    sx={{ 
                      width: isMobile ? 32 : 36, 
                      height: isMobile ? 32 : 36,
                      border: `2px solid ${brandColors.primary.main}30`,
                      transition: 'all 0.3s ease',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: isMobile ? 32 : 36,
                      height: isMobile ? 32 : 36,
                      background: brandColors.primary.gradient,
                    }}
                  >
                    <AccountCircle sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Enhanced User Menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 8px 32px rgba(0,0,0,0.25))',
                mt: 1.5,
                minWidth: 280,
                borderRadius: 4,
                background: theme.palette.background.glass,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: darkMode 
                  ? '1px solid rgba(241, 245, 249, 0.1)' 
                  : '1px solid rgba(30, 41, 59, 0.1)',
                '& .MuiAvatar-root': {
                  width: 40,
                  height: 40,
                  ml: -0.5,
                  mr: 2,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 20,
                  width: 12,
                  height: 12,
                  bgcolor: theme.palette.background.glass,
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  border: darkMode 
                    ? '1px solid rgba(241, 245, 249, 0.1)' 
                    : '1px solid rgba(30, 41, 59, 0.1)',
                  borderBottom: 'none',
                  borderRight: 'none',
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {user && (
              <Fade in timeout={200}>
                <Box>
                  <MenuItem 
                    disabled
                    sx={{
                      py: 2,
                      px: 3,
                      background: `linear-gradient(135deg, ${brandColors.primary.main}10, ${brandColors.accent.main}10)`,
                      border: `1px solid ${brandColors.primary.main}20`,
                      borderRadius: 2,
                      m: 1,
                      mb: 2,
                    }}
                  >
                    <Avatar 
                      src={user.avatar}
                      sx={{
                        background: brandColors.primary.gradient,
                        border: `2px solid ${brandColors.primary.main}30`,
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {user.name || 'KashLegal User'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email || 'user@kashlegal.com'}
                      </Typography>
                      <Chip
                        label="Admin"
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          background: brandColors.gold.gradient,
                          color: 'white',
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <Divider sx={{ mx: 1, mb: 1 }} />
                </Box>
              </Fade>
            )}
            
            <MenuItem 
              onClick={() => {}}
              sx={{
                py: 1.5,
                px: 3,
                mx: 1,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(59, 130, 246, 0.1)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon>
                <Settings fontSize="small" sx={{ color: brandColors.primary.main }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Settings & Preferences
                </Typography>
              </ListItemText>
            </MenuItem>

            <MenuItem 
              onClick={() => {}}
              sx={{
                py: 1.5,
                px: 3,
                mx: 1,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(59, 130, 246, 0.1)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon>
                <TrendingUp fontSize="small" sx={{ color: brandColors.accent.main }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Performance Analytics
                </Typography>
              </ListItemText>
            </MenuItem>
            
            <Divider sx={{ mx: 1, my: 1 }} />
            
            <MenuItem 
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 3,
                mx: 1,
                mb: 1,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(220, 38, 38, 0.1)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" sx={{ color: brandColors.secondary.main }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" sx={{ fontWeight: 500, color: brandColors.secondary.main }}>
                  Sign Out
                </Typography>
              </ListItemText>
            </MenuItem>
          </Menu>

          {/* Enhanced Notification Panel */}
          <NotificationPanel
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            notifications={notifications}
          />
        </Toolbar>
      </AppBar>
    </Slide>
  );
}

export default Header;